import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../modules/Authentication';
import ProtectedRoute from './ProtectedRoute';
import LoginPage from '../pages/LoginPage';
import { lazyLoad, LazyLoadError, withErrorBoundary } from '../common/components';
import { ErrorBoundaryKYC } from '../common/components/ErrorBoundaryKYC';
import UserLayout from '../layouts/UserLayout';
import AdminLayout from '../layouts/AdminLayout';
import OperatorLayout from '../layouts/OperatorLayout';
import { FeatureFlagGate } from '../modules/FeatureFlags';
import { FeatureFlagName } from '../modules/FeatureFlags/constants/featureFlagNames';
import { Callout } from '@neshane/blueberry';
const boundLazy = (loader) => withErrorBoundary()(lazyLoad({ loader, fallback: LazyLoadError }));
const BoundLoginPage = withErrorBoundary()(LoginPage);
const BoundUserLayout = withErrorBoundary()(UserLayout);
const BoundAdminLayout = withErrorBoundary()(AdminLayout);
const BoundOperatorLayout = withErrorBoundary()(OperatorLayout);
const DashboardPage = boundLazy(() => import('../pages/user/DashboardPage'));
const CertificatesPage = boundLazy(() => import('../pages/user/CertificatesPage'));
const SignPDFPage = boundLazy(() => import('../pages/user/SignPDFPage'));
const SigningHistoryPage = boundLazy(() => import('../pages/user/SigningHistoryPage'));
const PaymentsPage = boundLazy(() => import('../pages/user/PaymentsPage'));
const ProfilePage = boundLazy(() => import('../modules/Profile/ProfilePage'));
const UserActivityLogsPage = boundLazy(() => import('../pages/user/ActivityLogsPage'));
const UserTicketsPage = boundLazy(() => import('../pages/user/TicketsPage'));
const UserTicketDetailPage = boundLazy(() => import('../pages/user/TicketDetailPage'));
const EvidencePage = boundLazy(() => import('../pages/user/EvidencePage'));
const AdminDashboardPage = boundLazy(() => import('../pages/admin/AdminDashboardPage'));
const UsersPage = boundLazy(() => import('../pages/admin/UsersPage'));
const UserDetailsPage = boundLazy(() => import('../pages/admin/UserDetailsPage'));
const UserGroupsPage = boundLazy(() => import('../pages/admin/UserGroupsPage'));
const OperatorsPage = boundLazy(() => import('../pages/admin/OperatorsPage'));
const OperatorDetailsPage = boundLazy(() => import('../pages/admin/OperatorDetailsPage'));
const ActivityLogsPage = boundLazy(() => import('../pages/admin/ActivityLogsPage'));
const AdminTicketsPage = boundLazy(() => import('../pages/admin/TicketsPage'));
const OperatorTicketsPage = boundLazy(() => import('../pages/operator/TicketsPage'));
const OperatorDashboardPage = boundLazy(() => import('../pages/operator/OperatorDashboardPage'));
const EkycQueuePage = boundLazy(() => import('../pages/operator/EkycQueuePage'));
const OperatorActivityLogsPage = boundLazy(() => import('../pages/operator/ActivityLogsPage'));
const EvidenceReviewPage = boundLazy(() => import('../pages/admin/EvidenceReviewPage'));
const AllEvidencePage = boundLazy(() => import('../pages/admin/AllEvidencePage'));
const AdminPaymentsPage = boundLazy(() => import('../pages/admin/AdminPaymentsPage'));
const AdminCertificatesPage = boundLazy(() => import('../pages/admin/AdminCertificatesPage'));
const CertificateInstallPage = boundLazy(() => import('../pages/admin/CertificateInstallPage'));
const ProfilesPage = boundLazy(() => import('../pages/admin/ProfilesPage'));
const ProductsPage = boundLazy(() => import('../pages/admin/ProductsPage'));
const CurrencySettingsPage = boundLazy(() => import('../pages/admin/CurrencySettingsPage'));
const TermsManagementPage = boundLazy(() => import('../pages/admin/TermsManagementPage'));
const KycLinkPage = boundLazy(() => import('../pages/public/KycLinkPage'));
const PaymentLinkPage = boundLazy(() => import('../pages/public/PaymentLinkPage'));
const GuidePage = boundLazy(() => import('../pages/public/GuidePage'));
const HelpPage = boundLazy(() => import('../pages/public/HelpPage'));
const OperationsDashboardPage = boundLazy(() => import('../pages/admin/OperationsDashboardPage'));
const OperationsQueuePage = boundLazy(() => import('../pages/admin/OperationsQueuePage'));
const OperationsDlqPage = boundLazy(() => import('../pages/admin/OperationsDlqPage'));
const CompensationJobsPage = boundLazy(() => import('../pages/admin/CompensationJobsPage'));
const OperationsHealthPage = boundLazy(() => import('../pages/admin/OperationsHealthPage'));

/**
 * App Routes
 * Defines all application routes
 */
function AppRoutes() {
    const { isAuthenticated } = useAuth();

    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <BoundLoginPage />
                }
            />
            <Route
                path="/admin/login"
                element={
                    isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <BoundLoginPage panel="admin" />
                }
            />
            <Route
                path="/operator/login"
                element={
                    isAuthenticated ? <Navigate to="/operator/dashboard" replace /> : <BoundLoginPage panel="operator" />
                }
            />

            {/* User Routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <BoundUserLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route
                    path="certificates"
                    element={
                        <FeatureFlagGate
                            flag={FeatureFlagName.CERTIFICATE_ISSUE_ENABLED}
                            fallback={
                                <div style={{ padding: 24 }}>
                                    <Callout variant="warning" title="صدور گواهی غیرفعال است">سرویس صدور گواهی در حال حاضر غیرفعال است.</Callout>
                                </div>
                            }
                        >
                            <CertificatesPage />
                        </FeatureFlagGate>
                    }
                />
                <Route
                    path="sign-pdf"
                    element={
                        <FeatureFlagGate
                            flag={FeatureFlagName.PDF_SIGNING_ENABLED}
                            fallback={
                                <div style={{ padding: 24 }}>
                                    <Callout variant="warning" title="امضای PDF غیرفعال است">سرویس امضای PDF در حال حاضر غیرفعال است.</Callout>
                                </div>
                            }
                        >
                            <SignPDFPage />
                        </FeatureFlagGate>
                    }
                />
                <Route
                    path="signing-history"
                    element={
                        <FeatureFlagGate
                            flag={FeatureFlagName.PDF_SIGNING_ENABLED}
                            fallback={
                                <div style={{ padding: 24 }}>
                                    <Callout variant="warning" title="تاریخچه امضا غیرفعال است">سرویس امضای PDF غیرفعال است.</Callout>
                                </div>
                            }
                        >
                            <SigningHistoryPage />
                        </FeatureFlagGate>
                    }
                />
                <Route
                    path="payments"
                    element={
                        <FeatureFlagGate
                            flag={FeatureFlagName.PAYMENTS_ENABLED}
                            fallback={
                                <div style={{ padding: 24 }}>
                                    <Callout variant="warning" title="پرداخت غیرفعال است">
                                        سرویس پرداخت در حال حاضر غیرفعال است. لطفاً بعداً تلاش کنید.
                                    </Callout>
                                </div>
                            }
                        >
                            <PaymentsPage />
                        </FeatureFlagGate>
                    }
                />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="profile/security" element={<ProfilePage />} />
                <Route path="profile/roles" element={<ProfilePage />} />
                <Route path="security" element={<ProfilePage />} />
                <Route path="activity-logs" element={<UserActivityLogsPage />} />
                <Route
                    path="tickets"
                    element={
                        <FeatureFlagGate flag={FeatureFlagName.TICKETING_ENABLED} fallback={<div style={{ padding: 24 }}><Callout variant="warning" title="تیکت غیرفعال است">سرویس تیکت در حال حاضر غیرفعال است.</Callout></div>}>
                            <UserTicketsPage />
                        </FeatureFlagGate>
                    }
                />
                <Route
                    path="tickets/:ticketId"
                    element={
                        <FeatureFlagGate flag={FeatureFlagName.TICKETING_ENABLED} fallback={<div style={{ padding: 24 }}><Callout variant="warning" title="تیکت غیرفعال است">سرویس تیکت در حال حاضر غیرفعال است.</Callout></div>}>
                            <UserTicketDetailPage />
                        </FeatureFlagGate>
                    }
                />
                <Route
                    path="evidence"
                    element={
                        <FeatureFlagGate flag={FeatureFlagName.EVIDENCE_ENABLED} fallback={<div style={{ padding: 24 }}><Callout variant="warning" title="مدارک غیرفعال است">سرویس مدارک در حال حاضر غیرفعال است.</Callout></div>}>
                            <EvidencePage />
                        </FeatureFlagGate>
                    }
                />
            </Route>

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute requiredRole="admin">
                        <BoundAdminLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:userId" element={<UserDetailsPage />} />
                <Route path="user-groups" element={<UserGroupsPage />} />
                <Route path="operators" element={<OperatorsPage />} />
                <Route path="operators/:operatorId" element={<OperatorDetailsPage />} />
                <Route path="ekyc-queue" element={<EkycQueuePage />} />
                <Route path="evidence-review" element={<EvidenceReviewPage />} />
                <Route path="all-evidence" element={<AllEvidencePage />} />
                <Route path="certificates" element={<AdminCertificatesPage />} />
                <Route path="certificate-profiles" element={<ProfilesPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="settings" element={<CurrencySettingsPage />} />
                <Route path="terms" element={<TermsManagementPage />} />
                <Route path="payments" element={<AdminPaymentsPage />} />
                <Route path="certificate-install/:userId" element={<CertificateInstallPage />} />
                <Route path="activity-logs" element={<ActivityLogsPage />} />
                <Route path="tickets" element={<AdminTicketsPage />} />
                <Route path="operations" element={<OperationsDashboardPage />} />
                <Route path="operations/health" element={<OperationsHealthPage />} />
                <Route path="operations/queue" element={<OperationsQueuePage />} />
                <Route path="operations/dlq" element={<OperationsDlqPage />} />
                <Route path="operations/compensation" element={<CompensationJobsPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="profile/roles" element={<ProfilePage />} />
            </Route>

            {/* Operator Routes */}
            <Route
                path="/operator"
                element={
                    <ProtectedRoute requiredRole="operator">
                        <BoundOperatorLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/operator/dashboard" replace />} />
                <Route path="dashboard" element={<OperatorDashboardPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:userId" element={<UserDetailsPage />} />
                <Route path="ekyc-queue" element={<EkycQueuePage />} />
                <Route path="evidence-review" element={<EvidenceReviewPage />} />
                <Route path="payments" element={<AdminPaymentsPage />} />
                <Route path="certificate-install/:userId" element={<CertificateInstallPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="profile/roles" element={<ProfilePage />} />
                <Route path="activity-logs" element={<OperatorActivityLogsPage />} />
                <Route path="tickets" element={<OperatorTicketsPage />} />
            </Route>

            {/* Public KYC link (SMS flow) - no auth */}
            <Route
                path="/kyc"
                element={
                    isAuthenticated ? (
                        <ErrorBoundaryKYC>
                            <KycLinkPage />
                        </ErrorBoundaryKYC>
                    ) : (
                        <Navigate to="/login" replace />
                    )
                }
            />
            <Route
                path="/kyc/:token"
                element={
                    <ErrorBoundaryKYC>
                        <KycLinkPage />
                    </ErrorBoundaryKYC>
                }
            />

            {/* Public payment link (SMS flow) - no auth */}
            <Route
                path="/pay/:accessToken"
                element={
                    <FeatureFlagGate
                        flag={FeatureFlagName.PAYMENT_LINK_ENABLED}
                        fallback={
                            <div style={{ padding: 24 }}>
                                <Callout variant="warning" title="لینک پرداخت غیرفعال است">سرویس لینک پرداخت در حال حاضر غیرفعال است.</Callout>
                            </div>
                        }
                    >
                        <ErrorBoundaryKYC>
                            <PaymentLinkPage />
                        </ErrorBoundaryKYC>
                    </FeatureFlagGate>
                }
            />

            {/* Public guide & help pages (no auth required) */}
            <Route path="/guide/:role" element={<GuidePage />} />
            <Route path="/help/:role" element={<HelpPage />} />

            {/* Catch all - redirect to dashboard or login */}
            <Route
                path="*"
                element={
                    <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
                }
            />
        </Routes>
    );
}

export default AppRoutes;